import { useState, useEffect, useCallback } from 'react';
import { useErrorHandler } from '../../utils/useErrorHandler';
import { MunicipalityStats, TransactionHistoryItem } from '../types';
import { TRANSACTION_HISTORY_MANAGEMENT_CONSTANTS } from '../constants/transactionHistoryManagement';
import dataProvider from '../../services/api';

export const useTransactionHistory = () => {
  const { handleErrorWithLogout } = useErrorHandler();

  const [transactions, setTransactions] = useState<TransactionHistoryItem[]>([]);
  const [municipalities, setMunicipalities] = useState<MunicipalityStats[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalTransactions, setTotalTransactions] = useState<number>(0);
  const [transactionTypes, setTransactionTypes] = useState<string[]>([]);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState<string[]>([]);
  const [transactionsPerPage, setTransactionsPerPage] = useState<number>(
    TRANSACTION_HISTORY_MANAGEMENT_CONSTANTS.DEFAULT_TRANSACTIONS_PER_PAGE
  ); 

  const handleTransactionsPerPageChange = useCallback((newTransactionsPerPage: number) => {
    if (newTransactionsPerPage !== transactionsPerPage) {
      setTransactionsPerPage(newTransactionsPerPage);
      setCurrentPage(1); 
    }
  }, [transactionsPerPage]);

  const fetchData = useCallback(async () => {
    try {
     
      const { data: transactionData, total } = await dataProvider.getUserTransactionsByQuery({
        filter: searchTerm,
        transactionTypes: selectedTransactionTypes.length > 0 ? selectedTransactionTypes.join(',') : undefined,
        page: currentPage - 1,
        perPage: transactionsPerPage,
      });
      setTransactions(transactionData || []);
      setTotalTransactions(total || 0);

      // Fetch municipality stats
      const { data: municipalityData } = await dataProvider.getReportMunicipalities();
      setMunicipalities(municipalityData || []);
    } catch (error: any) {
      handleErrorWithLogout(error);
      setTransactions([]);
      setTotalTransactions(0);
      setMunicipalities([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedTransactionTypes, transactionsPerPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, Math.ceil(totalTransactions / transactionsPerPage))));
  }, [totalTransactions, transactionsPerPage]);

  const handleSearchChange = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1); // Reset to first page when searching
  }, []);

  const handleTransactionTypeFilter = useCallback((types: string[]) => {
    setSelectedTransactionTypes(types);
    setCurrentPage(1); // Reset to first page when filtering
  }, []);

  return {
    transactions,
    municipalities,
    loading,
    searchTerm,
    setSearchTerm: handleSearchChange,
    currentPage,
    totalTransactions,
    transactionsPerPage,
    transactionTypes,
    selectedTransactionTypes,
    handlePageChange,
    handleTransactionTypeFilter,
    fetchData,
    onTransactionsPerPageChange: handleTransactionsPerPageChange, // Export callback
  };
};